import { tx, txt, useContent } from "@/content/ContentContext";
import { useState } from "react";
import { QrLoginPanel } from "@/components/QrLogin";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, KeyRound, QrCode } from "lucide-react";
import AuthLayout from "@/components/AuthLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { isOnline, probe } from "@/lib/connectivity";
import { offlineLogin } from "@/lib/offlineAuth";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  useContent();
  const { signIn, signInOffline } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [mode, setMode] = useState(() => (window.matchMedia("(min-width: 768px)").matches ? "qr" : "password"));
  const qrIn = (data) => { signIn(data); nav("/pos"); };
  const chip = "mx-auto mt-4 flex h-9 w-fit items-center gap-2 rounded-full border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-700 shadow-sm transition-colors hover:border-slate-300 hover:text-slate-900";

  const goOffline = async (creds) => {
    const r = await offlineLogin(creds.identifier, creds.password);
    if (r.error) return setError(r.error);
    signInOffline(r.session, creds);
    nav("/pos");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const creds = { identifier: email, password };
    try {
      if (!isOnline() && !(await probe())) return await goOffline(creds);
      try {
        const { data } = await api.post("/auth/login", { identifier: email, password });
        signIn(data, creds);
        nav("/pos");
      } catch (err) {
        if (!err.response) return await goOffline(creds);
        setError(errMsg(err));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout compact={mode === "qr"} title={tx("login.back_to_the_counter")} subtitle={tx("login.owners_use_their_email_staff")}>
      {mode === "qr" ? (
        <div data-testid="login-qr-mode">
          <QrLoginPanel onSession={qrIn} large />
          <button type="button" onClick={() => setMode("password")} className={chip} data-testid="login-use-password-chip">
            <KeyRound className="h-3.5 w-3.5" />{txt("login.qr_use_password")}
          </button>
        </div>
      ) : (
      <div data-testid="login-password-mode">
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <div className="space-y-2">
          <Label htmlFor="em">{txt("login.email_or_username")}</Label>
          <Input id="em" type="text" autoCapitalize="none" autoComplete="username" placeholder={tx("login.owner_shop_com_or_staff")} className="h-12" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="pw">{txt("login.password")}</Label>
          <Input id="pw" type="password" autoComplete="current-password" className="h-12" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" />
        </div>
        {error && <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm font-medium text-rose-700" data-testid="login-error">{error}</div>}
        <button
          type="submit"
          disabled={!email || !password || busy}
          data-testid="login-submit-button"
          className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-slate-900 font-semibold text-white transition-[background-color,transform] hover:bg-slate-800 active:scale-[0.99] disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Log in
        </button>
        <p className="text-center text-sm text-slate-500">
          {txt("login.new_shop")} <Link to="/signup" className="font-semibold text-slate-900 underline decoration-amber-500 decoration-2 underline-offset-4" data-testid="go-to-signup-link">{txt("login.start_a_15_day_demo")}</Link>
        </p>
      </form>
      <button type="button" onClick={() => setMode("qr")} className={chip} data-testid="login-with-qr-link">
        <QrCode className="h-3.5 w-3.5" />{txt("login.qr_link")}
      </button>
      </div>
      )}
    </AuthLayout>
  );
}
